/**
 * MODULE: _shared/frontend/lib/frontend/utils/emailTemplateCreateBridge.ts
 * PURPOSE: Open golden-path EmailTemplateCreateModal from dock, overlay, Sales Hub, or context chips app-wide.
 * RECENT: 2026-06-06 [2.2.1328] — brain context refresh subscriber; post-save coaching event; coachInAssistant option.
 */

import { getApiBaseUrl, getAuthHeaders } from '../api/config';

export type EmailTemplateCreatePrefill = {
  name?: string;
  subject?: string;
  description?: string;
  bodyContent?: string;
  category?: string;
};

export type EmailTemplateCreateSuccessHint = 'sales_hub_library' | 'templates_library';

export type EmailTemplateSavedDetail = {
  successHint?: EmailTemplateCreateSuccessHint;
  mode?: 'create' | 'edit';
  templateTitle?: string;
  /** When true, active assistant thread shows post-save coaching. */
  coachInAssistant?: boolean;
};

export type EmailTemplateCreateOpenOptions = {
  prefill?: EmailTemplateCreatePrefill;
  /** Optional post-save side effect for module-specific navigation or refetch. */
  successHint?: EmailTemplateCreateSuccessHint;
  coachInAssistant?: boolean;
};

export const OPEN_EMAIL_TEMPLATE_CREATE_EVENT = 'brazn:open-email-template-create';
export const EMAIL_TEMPLATE_CREATED_EVENT = 'brazn:email-template-created';
export const ASSISTANT_BRAIN_CONTEXT_REFRESH_EVENT = 'brazn:assistant-brain-context-refresh';

/** Request the app-level EmailTemplateCreateModal (mounted in AppLayout). */
export function requestOpenEmailTemplateCreate(options?: EmailTemplateCreateOpenOptions): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<EmailTemplateCreateOpenOptions>(OPEN_EMAIL_TEMPLATE_CREATE_EVENT, {
      detail: options ?? {},
    })
  );
}

export function subscribeOpenEmailTemplateCreate(
  listener: (options: EmailTemplateCreateOpenOptions) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<EmailTemplateCreateOpenOptions>).detail ?? {};
    listener(detail);
  };
  window.addEventListener(OPEN_EMAIL_TEMPLATE_CREATE_EVENT, handler);
  return () => window.removeEventListener(OPEN_EMAIL_TEMPLATE_CREATE_EVENT, handler);
}

export function notifyEmailTemplateCreated(detail?: EmailTemplateSavedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<EmailTemplateSavedDetail>(EMAIL_TEMPLATE_CREATED_EVENT, {
      detail: detail ?? {},
    })
  );
}

export function subscribeEmailTemplateCreated(
  listener: (detail: EmailTemplateSavedDetail) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<EmailTemplateSavedDetail>).detail ?? {};
    listener(detail);
  };
  window.addEventListener(EMAIL_TEMPLATE_CREATED_EVENT, handler);
  return () => window.removeEventListener(EMAIL_TEMPLATE_CREATED_EVENT, handler);
}

export function requestAssistantBrainContextRefresh(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ASSISTANT_BRAIN_CONTEXT_REFRESH_EVENT));
}

export function subscribeAssistantBrainContextRefresh(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = () => listener();
  window.addEventListener(ASSISTANT_BRAIN_CONTEXT_REFRESH_EVENT, handler);
  return () => window.removeEventListener(ASSISTANT_BRAIN_CONTEXT_REFRESH_EVENT, handler);
}

/** Force world-model rebuild + brain context chip refresh after template create/edit/send. */
export function triggerEmailTemplateLearningRefresh(): void {
  const base = getApiBaseUrl().replace(/\/$/, '');
  void fetch(`${base}/api/v1/assistant/world-model/refresh?force=true`, {
    method: 'POST',
    headers: getAuthHeaders(),
  }).catch(() => undefined);
  requestAssistantBrainContextRefresh();
}
